import { Twitter, Facebook, MessageCircle, Mail, Share2 } from "lucide-react";
import { SURVIVOR_PORTRAITS } from "./survivorPortraits";
import { SHARE_URL } from "./siteAssets";

/**
 * Reusable survivor stories grid.
 *
 * Props:
 *   - kicker: small uppercase label
 *   - heading: large title
 *   - sub: italic subtitle
 *   - survivors: [{ name, place, note, quote }]
 *   - bg: optional background tailwind classes
 *   - survivorLabel: "Survivor" word, translated
 *   - share: { label, x, facebook, whatsapp, email, message(name, place), emailSubject(name) }
 */
export default function SurvivorStoriesSection({
  kicker,
  heading,
  sub,
  survivors,
  bg = "bg-[#F4EDE0] border-t border-[#2A1F15]/10",
  survivorLabel = "Survivor",
  share,
}) {
  return (
    <section id="survivors" className={`py-24 md:py-32 ${bg}`}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold text-center">
          {kicker}
        </div>
        <h2
          className="mt-6 text-5xl md:text-7xl leading-[1] text-center text-[#2A1F15]"
          style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
          data-testid="stories-survivors-title"
        >
          {heading}
        </h2>
        <p
          className="mt-8 max-w-2xl mx-auto text-center text-base md:text-lg italic text-[#2A1F15]/80"
          style={{ fontFamily: "'Merriweather', serif" }}
        >
          {sub}
        </p>

        <div className="mt-16 space-y-10">
          {survivors.map((s, i) => {
            const portrait = SURVIVOR_PORTRAITS[s.name];
            const anchor = `survivor-${i}`;
            const deepLink = `${SHARE_URL}/survivor-story#${anchor}`;
            const message = share ? share.message(s.name, s.place) : "";
            const subject = share ? share.emailSubject(s.name) : "";

            return (
              <article
                key={i}
                id={anchor}
                data-testid={`stories-survivor-${i}`}
                className="relative scroll-mt-28 rounded-xl bg-white border-l-4 border-[#E3282B] shadow-sm hover:shadow-xl transition-shadow overflow-hidden"
              >
                <div className="absolute top-6 left-8 px-3 py-1 rounded-full bg-[#E3282B] text-white text-[10px] tracking-[0.3em] uppercase font-bold z-10">
                  {survivorLabel} · {String(i + 1).padStart(2, "0")}
                </div>
                <div className="grid grid-cols-12 gap-0">
                  {portrait && (
                    <div className="col-span-12 md:col-span-4 bg-[#2A1F15]">
                      <div className="aspect-[3/4] md:aspect-auto md:h-full overflow-hidden">
                        <img
                          src={portrait}
                          alt={`Portrait of ${s.name}, FGM survivor`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          data-testid={`stories-survivor-${i}-portrait`}
                        />
                      </div>
                    </div>
                  )}
                  <div className={portrait ? "col-span-12 md:col-span-8 p-8 md:p-12 pt-16 md:pt-12" : "col-span-12 p-8 md:p-12 pt-16"}>
                    <div className="mb-6">
                      <div
                        className="text-2xl md:text-3xl text-[#2A1F15]"
                        style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
                      >
                        {s.name}
                      </div>
                      <div className="mt-1 text-sm text-[#8C5A3C] font-semibold">{s.place}</div>
                      {s.note && (
                        <div className="mt-1 text-xs italic text-[#2A1F15]/60">{s.note}</div>
                      )}
                    </div>
                    <blockquote
                      className="text-base md:text-lg leading-[1.85] text-[#2A1F15]/90"
                      style={{ fontFamily: "'Merriweather', serif" }}
                    >
                      {s.quote ? `"${s.quote}"` : null}
                    </blockquote>

                    {share && (
                      <div className="mt-8 pt-6 border-t border-[#2A1F15]/10 flex flex-wrap items-center gap-3" data-testid={`stories-survivor-${i}-share`}>
                        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#8C5A3C] font-bold mr-2">
                          <Share2 size={12} /> {share.label}
                        </div>
                        <a
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(deepLink)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={share.x}
                          data-testid={`stories-survivor-${i}-share-x`}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-black text-white hover:scale-110 transition-transform shadow-sm"
                        >
                          <Twitter size={14} fill="white" />
                        </a>
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(deepLink)}&quote=${encodeURIComponent(message)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={share.facebook}
                          data-testid={`stories-survivor-${i}-share-facebook`}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#1877F2] text-white hover:scale-110 transition-transform shadow-sm"
                        >
                          <Facebook size={14} fill="white" />
                        </a>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(`${message} ${deepLink}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={share.whatsapp}
                          data-testid={`stories-survivor-${i}-share-whatsapp`}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#25D366] text-white hover:scale-110 transition-transform shadow-sm"
                        >
                          <MessageCircle size={14} fill="white" />
                        </a>
                        <a
                          href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${message}\n\n${deepLink}`)}`}
                          aria-label={share.email}
                          data-testid={`stories-survivor-${i}-share-email`}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#8C5A3C] text-white hover:scale-110 transition-transform shadow-sm"
                        >
                          <Mail size={14} />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
